// The crew manifest: every billet (role) aboard, and the cast member who holds it where the roster has one.
// A billet is authority and expertise, not a seat: whoever holds it can walk anywhere in the ship.
// Source: Docs/ROLES.md and the cast list in Docs/HANDOFF.md. Billets with no cast member are VACANT.

export type Department = "command" | "flight" | "operations" | "science" | "tactical" | "security" | "engineering" | "medical" | "counsel" | "supply" | "civilian";   // supply: the quartermasters and the cargo crew (shared/supply.ts); civilian: passengers, guests, contractors (plain clothes, no authority)

export interface Billet {
  id: string;
  title: string;
  department: Department;
  character: string | null;   // the cast member for this billet, or null when the roster has nobody
}

export const BILLETS: Billet[] = [
  { id: "captain", title: "Captain", department: "command", character: "Capt. Dur'El Grey" },
  { id: "xo", title: "First Officer", department: "command", character: "XO Abrasive Cider" },
  { id: "helm", title: "Helmsman", department: "flight", character: "Cmdr. Noalear" },
  { id: "copilot", title: "Co-pilot", department: "flight", character: "Lt. Cmdr. Dizchord" },
  { id: "ops", title: "Operations Officer", department: "operations", character: "Cmdr. Flex Bowman" },
  { id: "comms", title: "Communications Officer", department: "operations", character: "Lt. Cmdr. Gub" },
  { id: "science", title: "Science Officer", department: "science", character: "SFC Cosmora Vex" },
  { id: "tactical", title: "Tactical Officer", department: "tactical", character: "Capt. Broken Holmes" },
  { id: "security_chief", title: "Chief of Security", department: "security", character: "Commander Heisenburg" },
  { id: "security_1", title: "Security Crewman", department: "security", character: "Lt. Gamiel" },
  { id: "security_2", title: "Security Crewman", department: "security", character: null },
  { id: "chief_engineer", title: "Chief Engineer", department: "engineering", character: "Cmdr. Galendari" },
  { id: "mechanic", title: "Engineer", department: "engineering", character: "Lost Ai" },
  { id: "cmo", title: "Chief Medical Officer", department: "medical", character: "Capt. Dr. Vermin Marden" },
  { id: "medical_1", title: "Medical Officer", department: "medical", character: "Lt. Cmdr. Karliyel" },
  { id: "medical_2", title: "Medical Officer", department: "medical", character: "Ruth \"Fee\" Campbell" },
  { id: "corpsman", title: "Corpsman", department: "medical", character: "Ghiron Singh" },
  { id: "counselor", title: "Counselor", department: "counsel", character: "Lt. Marnellzibub" },   // (Grey, 2026-10-19: one of the main cast)
  // the quartermaster (Grey, 2026-10-17): the ship's stores, at the armory's issuing window; Supply its own department since the same
  // day ("They are their own class"): the Quartermaster heads it, the Quartermaster's Mate works the stores with them, the cargo
  // specialists work the hold (shared/supply.ts). Vacant on the roster: a crewman in person takes one at check-in, or the Pool posts one
  { id: "quartermaster", title: "Quartermaster", department: "supply", character: null },
  { id: "quartermaster_2", title: "Quartermaster's Mate", department: "supply", character: null },
  { id: "cargo_1", title: "Cargo Specialist", department: "supply", character: null },
  { id: "cargo_2", title: "Cargo Specialist", department: "supply", character: null },
  // under the Counselor (Grey, 2026-10-17): therapists (sessions, trauma) and morale officers (group activities); shared/morale.ts
  { id: "therapist_1", title: "Therapist", department: "counsel", character: null },
  { id: "therapist_2", title: "Therapist", department: "counsel", character: null },
  { id: "morale_1", title: "Morale Officer", department: "counsel", character: null },
  { id: "morale_2", title: "Morale Officer", department: "counsel", character: null },
];

// the roster's named general crew (Grey, 2026-10-17): aboard with no post, as NPC crew until the player is aboard
export const CAST_CREW: { id: string; name: string }[] = [];   // (Cpl. Desynchronize taken off the roster: Grey, 2026-10-27, "get rid of this desync crew")
export const CREW_BILLET = "crew";   // anyone can join as general crew, unlimited
export const CIVILIAN_BILLET = "civilian";                       // passengers and guests: no authority, no station, unlimited
// the names that make a post a civilian one (an admin's "Civilian", "Passenger", "Guest"...): outside the chain of command
export const CIVILIAN_WORDS = /\b(civilians?|passengers?|guests?|visitors?|contractors?|dependents?|tourists?)\b/i;
export const OPEN_BILLETS = [CREW_BILLET, CIVILIAN_BILLET];
export const CIVILIAN_UNIFORM = { body: "#6b7a8f", trim: "#2a2f3a" };   // plain clothes

// uniform colours, from the bridge spec's colour code where it has one
export const UNIFORM: Record<Department | "crew", { body: string; trim: string }> = {
  command: { body: "#d9a520", trim: "#111111" },
  flight: { body: "#d9a520", trim: "#222222" },
  operations: { body: "#c99a2a", trim: "#333333" },
  science: { body: "#2bb3a8", trim: "#14324a" },
  tactical: { body: "#20232a", trim: "#c0c6cf" },
  security: { body: "#b3262e", trim: "#111111" },
  engineering: { body: "#1d2b4f", trim: "#e8772e" },
  medical: { body: "#6b3fa0", trim: "#e9e3f2" },
  counsel: { body: "#3c6e91", trim: "#dfe8f0" },
  supply: { body: "#c99a2a", trim: "#333333" },   // operations' uniform, as the quartermaster wore before Supply (the uniforms are frozen until Grey says)
  civilian: CIVILIAN_UNIFORM,
  crew: { body: "#6b7380", trim: "#2a2f37" },
};

export function departmentOf(billetId: string): Department | "crew" {
  return BILLETS.find((b) => b.id === billetId)?.department ?? "crew";
}

export function billetTitle(billetId: string): string {
  return BILLETS.find((b) => b.id === billetId)?.title ?? ({ civilian: "Civilian", prisoner: "Prisoner", survivor: "Survivor", raider: "Boarder" } as Record<string, string>)[billetId] ?? "Crew";
}

// Bridge crew: the billets with a position on the bridge. They can work the bridge screens unless the officer in
// command takes that away, and the officer in command can grant it to anyone else.
export const BRIDGE_CREW = new Set(["captain", "xo", "cmo", "helm", "copilot", "science", "comms", "tactical", "ops", "chief_engineer"]);

// ---------- chain of command: by type, then by rank ----------
// The officer in command (OIC) is whoever stands highest of the people aboard. Types, in order: flag officers (a fixed
// order), bridge crew, department executive officers (DXOs), lower decks. Within a type, higher rank first; equal rank
// keeps the manifest order. General crew (no billet) are not in the chain.
export type CommandType = "flag" | "bridge" | "dxo" | "lower";
export const COMMAND_TYPES: CommandType[] = ["flag", "bridge", "dxo", "lower"];
export const FLAG_ORDER = ["captain", "xo", "cmo", "tactical", "chief_engineer"];          // Capt, XO, CMO, TAO, Chief Engineer
export const COMMAND_TYPE: Record<string, CommandType> = {
  captain: "flag", xo: "flag", cmo: "flag", tactical: "flag", chief_engineer: "flag",
  helm: "bridge", copilot: "bridge", ops: "bridge", comms: "bridge", science: "bridge",
  security_chief: "dxo", counselor: "dxo", quartermaster: "dxo",   // the Quartermaster heads Supply
  security_1: "lower", security_2: "lower", mechanic: "lower", medical_1: "lower", medical_2: "lower", corpsman: "lower",
  quartermaster_2: "lower", cargo_1: "lower", cargo_2: "lower",
  therapist_1: "lower", therapist_2: "lower", morale_1: "lower", morale_2: "lower",
};
// rank of each billet, from its cast member's title (Capt., Cmdr., Lt. Cmdr., Lt., SPC); a vacant billet takes a default
export const RANKS = ["Civilian", "Specialist", "Ensign", "Lieutenant (jg)", "Lieutenant", "Lieutenant Commander", "Commander", "Captain"];
const RANK_OF: Record<string, number> = {
  captain: 7, xo: 6, cmo: 7, tactical: 7, chief_engineer: 6,
  helm: 6, copilot: 5, ops: 6, comms: 5, science: 1,
  security_chief: 6, counselor: 4,
  security_1: 4, security_2: 2, mechanic: 4, medical_1: 5, medical_2: 4, corpsman: 1,
  quartermaster: 4, quartermaster_2: 2, cargo_1: 1, cargo_2: 1,   // Supply's head a Lieutenant (an Ensign before Supply), the mate an Ensign, the cargo crew Specialists
  therapist_1: 3, therapist_2: 3, morale_1: 2, morale_2: 2,
};
export const rankOf = (billet: string) => RANK_OF[billet] ?? -1;
// the drafted crew's grades (Grey, 2026-10-16: officers apart from the enlisted, and the enlisted as senior enlisted, NCOs and
// lower enlisted), three ranks each, lowest first. The officers' are RANKS' own; GRADE_RANK_NO places every rank in RANKS'
// order for sorting (and the collar's pips), the enlisted below the officers, Specialist (1) between the NCOs and the chiefs
export type Grade = "officer" | "senior" | "nco" | "lower";
export const GRADES: Grade[] = ["officer", "senior", "nco", "lower"];
export const GRADE_LABEL: Record<Grade, string> = { officer: "Officers", senior: "Senior enlisted", nco: "NCOs", lower: "Lower enlisted" };
export const GRADE_ONE: Record<Grade, string> = { officer: "Officer", senior: "Senior enlisted", nco: "NCO", lower: "Lower enlisted" };   // one of them, on their file
export const GRADE_RANKS: Record<Grade, string[]> = {
  officer: ["Ensign", "Lieutenant (jg)", "Lieutenant"],
  senior: ["Chief Petty Officer", "Senior Chief Petty Officer", "Master Chief Petty Officer"],
  nco: ["Petty Officer 3rd Class", "Petty Officer 2nd Class", "Petty Officer 1st Class"],
  lower: ["Crewman Recruit", "Crewman Apprentice", "Crewman"],
};
export const GRADE_RANK_NO: Record<Grade, number[]> = { officer: [2, 3, 4], senior: [1.7, 1.8, 1.9], nco: [0.7, 0.8, 0.9], lower: [0.1, 0.2, 0.3] };
// does billet a outrank billet b? (higher in the chain of command; general crew outrank nobody)
// The Hierarchy chart (shared/hierarchy.ts) has its say first: once the officer in command has drawn the reporting lines, a post
// above another in the same line outranks it (and one below never does); where the chart says nothing (no lines drawn, or
// the two in different lines), the rank rules below decide, as they always have
export function outranksByRank(a: string, b: string): boolean {
  if (a === b || !COMMAND_TYPE[a]) return false; if (!COMMAND_TYPE[b]) return true;
  return chainOfCommand([a, b])[0] === a;
}
let outranksHook: ((a: string, b: string) => boolean | null) | null = null;
export function setOutranksHook(f: ((a: string, b: string) => boolean | null) | null) { outranksHook = f; }
export function outranks(a: string, b: string): boolean {
  if (a === b) return false;
  const said = outranksHook ? outranksHook(a, b) : null;
  return said ?? outranksByRank(a, b);
}
// crew types a station can be opened to: departments, plus general crew
export const CREW_TYPES = ["security", "operations", "science", "medical", "engineering", "flight", "counsel", "supply", "crew"] as const;
export const typeOf = (billet: string): string => (billet === "crew" ? "crew" : departmentOf(billet));
// does billet a outrank everyone of this crew type?
export function outranksType(a: string, type: string): boolean {
  if (type === "crew") return !!COMMAND_TYPE[a];
  const list = BILLETS.filter((b) => b.department === type); return list.length > 0 && list.every((b) => outranks(a, b.id));
}
// the billets held aboard, highest in command first
// typeFor: a billet's type as it stands now (whoever holds it designated a department XO counts as a DXO: shared/dxo.ts)
export function chainOfCommand(heldBillets: string[], typeFor?: (billet: string) => CommandType | undefined): string[] {
  const key = (b: string) => {
    const type = COMMAND_TYPES.indexOf(typeFor?.(b) ?? COMMAND_TYPE[b]);
    return [type, type === 0 ? FLAG_ORDER.indexOf(b) : -rankOf(b), BILLETS.findIndex((x) => x.id === b)];
  };
  return heldBillets.filter((b) => COMMAND_TYPE[b]).sort((a, b) => { const ka = key(a), kb = key(b); return ka[0] - kb[0] || ka[1] - kb[1] || ka[2] - kb[2]; });
}

// the admin's changes to the roles (server/admin.ts keeps them): a billet's title renamed, new billets added. Applied
// in place on the server at start and on every client as they arrive, so everything that reads BILLETS sees them
export interface RoleChanges { titles: Record<string, { title?: string }>; added: { id: string; title: string; department: string; rank: number }[] }
const ORIGINAL_TITLES = new Map(BILLETS.map((b) => [b.id, b.title]));
const DEPTS: readonly string[] = ["command", "flight", "operations", "science", "tactical", "security", "engineering", "medical", "counsel", "supply"];
export function applyRoleChanges(rc: RoleChanges) {
  for (const b of BILLETS) b.title = rc.titles[b.id]?.title ?? ORIGINAL_TITLES.get(b.id) ?? b.title;
  for (let i = BILLETS.length - 1; i >= 0; i--) if (!ORIGINAL_TITLES.has(BILLETS[i].id) && !rc.added.some((a) => a.id === BILLETS[i].id)) BILLETS.splice(i, 1);
  for (const a of rc.added) {
    if (ORIGINAL_TITLES.has(a.id)) continue;
    const title = rc.titles[a.id]?.title ?? a.title;
    // a civilian post (a passenger, a guest: by its department or its name) is outside the chain of command (Grey, 2026-10-17)
    const civ = a.department === "civilian" || CIVILIAN_WORDS.test(title) || CIVILIAN_WORDS.test(a.id.replace(/_/g, " "));
    const dept = (civ ? "civilian" : DEPTS.includes(a.department) ? a.department : "operations") as Department;
    const have = BILLETS.find((b) => b.id === a.id);
    if (have) { have.title = title; have.department = dept; } else BILLETS.push({ id: a.id, title, department: dept, character: null });
    RANK_OF[a.id] = a.rank;
    if (civ) delete COMMAND_TYPE[a.id];
    else COMMAND_TYPE[a.id] ??= a.rank >= 6 ? "dxo" : "lower";   // in the chain of command, by rank
  }
}
